import React, { useEffect, useRef, useState } from 'react';
import {
  formatPoCurrency,
  formatPoQuantity,
  searchPurchaseOrderLineItems,
} from '../../services/purchaseOrderService';

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return String(value); }
};

export default function PurchaseOrderItemSearch({ storeId }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open || !storeId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const data = await searchPurchaseOrderLineItems(storeId, query, { limit: 60 });
        setResults(data);
      } catch (err) {
        setError(err?.message || 'Search failed.');
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, open, storeId]);

  return (
    <section style={containerStyle}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={toggleStyle}
        aria-expanded={open}
      >
        <span>🔎 Search items in past orders</span>
        <span style={{ color: '#8B7B73', fontSize: 18, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
      </button>
      {open && (
        <div style={{ padding: '12px 16px 16px' }}>
          <div style={{ color: '#8B7B73', fontSize: 13, marginBottom: 8 }}>
            Find when an item was last ordered, the most recent quantity, and who placed the order.
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by description, brand, or location…"
            style={inputStyle}
          />
          {error && <div style={errorStyle}>{error}</div>}
          {loading && <div style={{ color: '#8B7B73', marginTop: 10 }}>Searching…</div>}
          {!loading && results.length === 0 && (
            <div style={{ color: '#8B7B73', marginTop: 10, fontSize: 13 }}>
              {query ? 'No items found.' : 'Type to search or leave empty to see the most recently ordered items.'}
            </div>
          )}
          {!loading && results.length > 0 && (
            <div style={{ marginTop: 10, overflowX: 'auto', border: '1px solid #E9DFD6', borderRadius: 10 }}>
              <table style={tableStyle}>
                <thead>
                  <tr style={{ background: '#FAF6F2' }}>
                    <th style={thStyle}>Item</th>
                    <th style={thStyle}>Vendor</th>
                    <th style={thStyle}>Last ordered</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Last qty</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Last price</th>
                    <th style={thStyle}>Ordered by</th>
                    <th style={thStyle}>PO #</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((row) => (
                    <tr key={row.key}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600, color: '#3D2518' }}>{row.description}</div>
                        {row.location && <div style={{ color: '#8B7B73', fontSize: 12 }}>{row.location}{row.brand ? ` · ${row.brand}` : ''}</div>}
                      </td>
                      <td style={tdStyle}>{row.latest.vendorName || '—'}</td>
                      <td style={tdStyle}>{formatDateTime(row.latest.sentAt)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatPoQuantity(row.latest.quantity)} {row.unit || ''}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatPoCurrency(row.latest.unitPrice)}</td>
                      <td style={tdStyle}>{row.latest.orderedByName || '—'}</td>
                      <td style={{ ...tdStyle, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}>{row.latest.poNumber || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

const containerStyle = { background: '#fff', border: '1.5px solid #E9DFD6', borderRadius: 12, marginBottom: 12 };
const toggleStyle = { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', border: 'none', background: 'transparent', fontFamily: 'Montserrat, sans-serif', fontSize: 14, fontWeight: 700, color: '#3D2518', cursor: 'pointer' };
const inputStyle = { width: '100%', padding: '10px 12px', border: '1.5px solid #E9DFD6', borderRadius: 10, fontSize: 14, color: '#3D2518', background: '#fff' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle = { padding: '8px 10px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#5A4A42', borderBottom: '1px solid #E9DFD6' };
const tdStyle = { padding: '6px 10px', borderBottom: '1px solid #f3ebe2', color: '#3D2518', verticalAlign: 'middle' };
const errorStyle = { color: '#b71c1c', background: '#fdecea', padding: '8px 12px', borderRadius: 8, marginTop: 10, fontSize: 13 };
